// game/bullets.interpolate.ts — Bullet interpolation and position tracking

import type { Bullet } from "@game/protocol";
import { playPositionalShootSound } from "../audio/audio";
import { smoothPosition, wrapCoordinate } from "./smoothing";

let state: any;

export function setBulletInterpolationState(gameState: any) {
  state = gameState;
}

export function processBulletUpdate(serverBullets: Bullet[]) {
  const currentTime = Date.now();
  const receivedBulletIds = new Set<string>();

  for (const bullet of serverBullets) {
    receivedBulletIds.add(bullet.id);

    let interpolation = state.bulletsInterpolation.get(bullet.id);

    if (!interpolation) {
      interpolation = {
        serverPos: { x: bullet.pos.x, y: bullet.pos.y },
        prevServerPos: { x: bullet.pos.x, y: bullet.pos.y },
        currentPos: { x: bullet.pos.x, y: bullet.pos.y },
        velocity: { x: bullet.vel.x || 0, y: bullet.vel.y || 0 },
        lastServerUpdate: currentTime,
        isNew: true,
        markedForRemoval: false,
        shooterScale: bullet.shooterScale,
        age: bullet.age || 0,
        trail: [],
        ownerId: bullet.ownerId,
      };
      state.bulletsInterpolation.set(bullet.id, interpolation);

      const isCurrentPlayer = bullet.ownerId === state.playerId;
      const currentPlayer = state.players.get(state.playerId || "");

      const playerPosition =
        currentPlayer?.pos &&
        typeof currentPlayer.pos.x === "number" &&
        typeof currentPlayer.pos.y === "number" &&
        !Number.isNaN(currentPlayer.pos.x) &&
        !Number.isNaN(currentPlayer.pos.y) &&
        Number.isFinite(currentPlayer.pos.x) &&
        Number.isFinite(currentPlayer.pos.y)
          ? { x: currentPlayer.pos.x, y: currentPlayer.pos.y }
          : null;
      playPositionalShootSound(
        bullet.pos.x,
        bullet.pos.y,
        playerPosition,
        state.world,
        isCurrentPlayer,
      );
    } else {
      interpolation.prevServerPos = { ...interpolation.serverPos };
      interpolation.serverPos = { x: bullet.pos.x, y: bullet.pos.y };

      interpolation.velocity.x = bullet.vel?.x || 0;
      interpolation.velocity.y = bullet.vel?.y || 0;

      interpolation.lastServerUpdate = currentTime;
      interpolation.isNew = false;
      interpolation.shooterScale = bullet.shooterScale;
      interpolation.age = bullet.age || 0;
      interpolation.ownerId = bullet.ownerId;

      if (!interpolation.trail) {
        interpolation.trail = [];
      }
    }
  }

  for (const [id, interpolation] of state.bulletsInterpolation) {
    if (!receivedBulletIds.has(id) && !interpolation.markedForRemoval) {
      interpolation.markedForRemoval = true;
    }
  }

  for (const [id, interpolation] of state.bulletsInterpolation) {
    if (interpolation.markedForRemoval) {
      const timeSinceUpdate = currentTime - interpolation.lastServerUpdate;
      if (timeSinceUpdate > 100) {
        state.bulletsInterpolation.delete(id);
      }
    }
  }
}

export function updateBulletPositions() {
  const currentTime = Date.now();

  for (const [_id, interpolation] of state.bulletsInterpolation) {
    if (interpolation.isNew || interpolation.markedForRemoval) {
      continue;
    }

    if (interpolation.trail && interpolation.currentPos) {
      interpolation.trail.push({
        pos: { x: interpolation.currentPos.x, y: interpolation.currentPos.y },
        timestamp: currentTime,
      });

      const maxTrailLength = 5;
      if (interpolation.trail.length > maxTrailLength) {
        interpolation.trail.shift();
      }

      const maxTrailAge = 100;
      interpolation.trail = interpolation.trail.filter(
        (point: { pos: { x: number; y: number }; timestamp: number }) =>
          currentTime - point.timestamp <= maxTrailAge,
      );
    }

    const timeSinceUpdate =
      (currentTime - interpolation.lastServerUpdate) / 1000;

    const maxExtrapolationTime = 0.1;
    const clampedTime = Math.min(timeSinceUpdate, maxExtrapolationTime);

    const predictedX =
      interpolation.serverPos.x + interpolation.velocity.x * clampedTime;
    const predictedY =
      interpolation.serverPos.y + interpolation.velocity.y * clampedTime;

    const targetX = wrapCoordinate(predictedX, state.world.w);
    const targetY = wrapCoordinate(predictedY, state.world.h);

    smoothPosition({
      current: interpolation.currentPos,
      target: { x: targetX, y: targetY },
      world: state.world,
    });

    interpolation.currentPos.x = wrapCoordinate(
      interpolation.currentPos.x,
      state.world.w,
    );
    interpolation.currentPos.y = wrapCoordinate(
      interpolation.currentPos.y,
      state.world.h,
    );
  }
}
